/**
 * useTrustActions — register-as-attester and attest-ledger-head mutations.
 *
 * Both mutations are online-only: the WebAuthn ceremony itself requires a
 * network round-trip to the gg-app server (to get challenge options) and a
 * second POST to submit the assertion. The ProvenancePanel gates these
 * buttons behind `useOnlineStatus` so they are never called offline.
 *
 * Register flow:
 *   1. POST /v1/attesters/register/options — get server challenge.
 *   2. navigator.credentials.create (Touch ID / Face ID / passkey).
 *   3. POST /v1/attesters/register — store credential server-side.
 *   Returns AttesterIdentity.
 *
 * Attest flow:
 *   1. POST /v1/vehicles/:id/attestations/options — server stamps createdAt
 *      and computes the challenge; client echoes createdAt verbatim.
 *   2. navigator.credentials.get (WebAuthn assertion using stored credId).
 *   3. POST /v1/vehicles/:id/attestations — persist the attestation.
 *   Returns Attestation.
 *
 * The credId for the attest step is currently an explicit caller-supplied
 * param. In a future iteration a stored preference or
 * POST /v1/attesters/me endpoint could supply it automatically.
 */

import { apiFetch } from '@/lib/api';
import { env } from '@/lib/env';
import { attestLedgerHead, registerAttester, type AttestationOptions } from '@/lib/trust/webauthn';
import type { AttestationOptionsRequest, AttestationSubmitRequest } from '@/types/api';
import type { AttesterIdentity } from '@/types/domain';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { proofBundleKeys } from './useProofBundle';

// ---------------------------------------------------------------------------
// Register mutation
// ---------------------------------------------------------------------------

export interface RegisterAttesterInput {
  /** Display label for the passkey (shown in the OS passkey prompt). */
  label: string;
}

/**
 * Mutation: register a new passkey as an attester.
 *
 * The rpId comes from `env.VITE_TRUST_RP_ID` — must match the current
 * hostname and gg-app GG_TRUST_RP_ID.
 */
export function useRegisterAttester(): UseMutationResult<
  AttesterIdentity,
  Error,
  RegisterAttesterInput
> {
  return useMutation<AttesterIdentity, Error, RegisterAttesterInput>({
    // WebAuthn ceremonies must never auto-retry — re-invoking
    // navigator.credentials without a fresh user gesture throws NotAllowedError.
    retry: false,
    mutationFn: async ({ label }) => {
      return registerAttester({
        rpId: env.VITE_TRUST_RP_ID,
        rpName: 'Absolute Pinks',
        label,
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Attest mutation
// ---------------------------------------------------------------------------

export interface AttestLedgerHeadInput {
  vehicleId: string;
  /** The expert's statement to bind. */
  statement: string;
  /** The attester's stored credential id (base64url, from AttesterIdentity). */
  credId: string;
}

/**
 * Mutation: perform a WebAuthn assertion and submit an attestation.
 *
 * The `createdAt` is read from the server-returned options and echoed verbatim
 * into the submission body. The client never generates its own timestamp here.
 */
export function useAttestLedgerHead(): UseMutationResult<void, Error, AttestLedgerHeadInput> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, AttestLedgerHeadInput>({
    // WebAuthn ceremonies must never auto-retry — re-invoking
    // navigator.credentials without a fresh user gesture throws NotAllowedError.
    retry: false,
    mutationFn: async ({ vehicleId, statement, credId }) => {
      // Step 1: fetch server-stamped options (challenge + createdAt).
      const optionsBody: AttestationOptionsRequest = { statement };
      const options = await apiFetch<AttestationOptions>(
        `/v1/vehicles/${vehicleId}/attestations/options`,
        { method: 'POST', body: optionsBody },
      );

      // Step 2: WebAuthn assertion ceremony.
      // Use server-returned rpId (may differ per env); echo createdAt verbatim.
      const components = await attestLedgerHead({
        rpId: options.rpId,
        credId,
        vehicleId,
        headHash: options.headHash,
        statement: options.statement,
        createdAt: options.createdAt, // server-stamped — do NOT regenerate
      });

      // Step 3: submit assertion to gg-app.
      const body: AttestationSubmitRequest = {
        attesterCredId: credId,
        vehicleId,
        headHash: options.headHash,
        statement: options.statement,
        createdAt: options.createdAt,
        authenticatorData: components.authenticatorData,
        clientDataJSON: components.clientDataJSON,
        signature: components.signature,
      };

      await apiFetch<void>(`/v1/vehicles/${vehicleId}/attestations`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: (_data, { vehicleId }) => {
      void queryClient.invalidateQueries({ queryKey: proofBundleKeys.forVehicle(vehicleId) });
    },
  });
}
